import { Component, input, output, forwardRef, signal, ElementRef, ViewChild, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { ComponentSize } from '../types';

export interface DropdownOption {
  label: string;
  value: any;
  disabled?: boolean;
  icon?: string;
  group?: string;
}

@Component({
  selector: 'app-dropdown',
  imports: [CommonModule, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownComponent),
      multi: true
    }
  ],
  template: `
    <div class="relative" #dropdownContainer>
      <label 
        [for]="id() || name()"
        [class]="labelClasses">
        @if (label()) {
          {{ label() }}
          @if (required()) {
            <span class="text-red-500 ml-1">*</span>
          }
        }
      </label>
      
      <div class="relative">
        <button
          type="button"
          [id]="id() || name()"
          [disabled]="disabled()"
          [class]="buttonClasses"
          (click)="toggleDropdown()"
          [attr.aria-expanded]="isOpen()"
          [attr.aria-haspopup]="'listbox'">
          <span class="flex items-center gap-2 flex-1 text-left">
            @if (selectedOption) {
              @if (selectedOption.icon) {
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="selectedOption.icon" />
                </svg>
              }
              <span>{{ selectedOption.label }}</span>
            } @else {
              <span class="text-gray-400 dark:text-gray-500">{{ placeholder() }}</span>
            }
          </span>
          <svg 
            class="w-4 h-4 transition-transform duration-200"
            [class.rotate-180]="isOpen()"
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        @if (isOpen()) {
          <div
            #dropdownMenu
            [class]="menuClasses"
            role="listbox">
            @if (filterable()) {
              <div class="p-2 border-b border-gray-200 dark:border-gray-700">
                <input
                  type="text"
                  [(ngModel)]="filterValue"
                  (ngModelChange)="onFilterChange()"
                  placeholder="Search..."
                  class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" />
              </div>
            }
            
            <div class="max-h-60 overflow-y-auto">
              @for (group of groupedOptions; track group.name || $index) {
                @if (group.name) {
                  <div class="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                    {{ group.name }}
                  </div>
                }
                @for (option of group.options; track option.value) {
                  <button
                    type="button"
                    role="option"
                    [disabled]="option.disabled"
                    [class]="getOptionClasses(option)"
                    (click)="selectOption(option)"
                    [attr.aria-selected]="isSelected(option)">
                    @if (option.icon) {
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="option.icon" />
                      </svg>
                    }
                    <span class="flex-1 text-left">{{ option.label }}</span>
                    @if (isSelected(option)) {
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                      </svg>
                    }
                  </button>
                }
              }
            </div>
          </div>
        }
      </div>
      
      @if (helpText() && !hasError) {
        <span class="text-xs text-gray-500 dark:text-gray-400 mt-1 block">
          {{ helpText() }}
        </span>
      }
      
      @if (hasError) {
        <span class="text-xs text-red-500 dark:text-red-400 mt-1 block font-medium">
          {{ displayError }}
        </span>
      }
    </div>
  `,
  styles: []
})
export class DropdownComponent implements ControlValueAccessor {
  label = input<string>('');
  placeholder = input<string>('Select an option');
  options = input<DropdownOption[]>([]);
  required = input<boolean>(false);
  disabled = input<boolean>(false);
  filterable = input<boolean>(false);
  errorMessage = input<string>('');
  helpText = input<string>('');
  id = input<string>('');
  name = input<string>('');
  class = input<string>('');
  size = input<ComponentSize>('md');

  changeEvent = output<any>();

  @ViewChild('dropdownContainer', { static: false }) dropdownContainer!: ElementRef<HTMLElement>;
  @ViewChild('dropdownMenu', { static: false }) dropdownMenu!: ElementRef<HTMLElement>;

  value = signal<any>(null);
  isOpen = signal(false);
  filterValue = '';
  touched = signal(false);

  private onChange = (value: any) => {};
  private onTouched = () => {};

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    if (!this.isOpen()) return;
    const target = event.target as HTMLElement;
    if (this.dropdownContainer?.nativeElement?.contains(target)) return;
    this.isOpen.set(false);
  }

  toggleDropdown(): void {
    if (!this.disabled()) {
      this.isOpen.update(v => !v);
    }
  }

  selectOption(option: DropdownOption): void {
    if (option.disabled) return;
    this.value.set(option.value);
    this.onChange(option.value);
    this.changeEvent.emit(option.value);
    this.isOpen.set(false);
    this.markAsTouched();
  }

  isSelected(option: DropdownOption): boolean {
    return this.value() === option.value;
  }

  onFilterChange(): void {
    // Filtering is handled by groupedOptions getter
  }

  get selectedOption(): DropdownOption | null {
    const val = this.value();
    return this.options().find(opt => opt.value === val) || null;
  }

  get filteredOptions(): DropdownOption[] {
    if (!this.filterValue) return this.options();
    const filter = this.filterValue.toLowerCase();
    return this.options().filter(opt => 
      opt.label.toLowerCase().includes(filter) ||
      (opt.group && opt.group.toLowerCase().includes(filter))
    );
  }

  get groupedOptions(): { name?: string; options: DropdownOption[] }[] {
    const filtered = this.filteredOptions;
    const groups = new Map<string, DropdownOption[]>();
    const ungrouped: DropdownOption[] = [];

    filtered.forEach(option => {
      if (option.group) {
        if (!groups.has(option.group)) {
          groups.set(option.group, []);
        }
        groups.get(option.group)!.push(option);
      } else {
        ungrouped.push(option);
      }
    });

    const result: { name?: string; options: DropdownOption[] }[] = [];
    groups.forEach((options, name) => {
      result.push({ name, options });
    });
    if (ungrouped.length > 0) {
      result.push({ options: ungrouped });
    }

    return result;
  }

  getOptionClasses(option: DropdownOption): string {
    const classes = [
      'w-full',
      'px-4',
      'py-2',
      'text-left',
      'flex',
      'items-center',
      'gap-2',
      'transition-colors',
      'duration-150',
      'focus:outline-none'
    ];

    if (option.disabled) {
      classes.push('opacity-50', 'cursor-not-allowed');
    } else {
      classes.push('hover:bg-gray-100', 'dark:hover:bg-gray-700', 'cursor-pointer');
    }

    if (this.isSelected(option)) {
      classes.push('bg-blue-50', 'dark:bg-blue-900/20', 'text-blue-600', 'dark:text-blue-400');
    } else {
      classes.push('text-gray-700', 'dark:text-gray-300');
    }

    return classes.join(' ');
  }

  get buttonClasses(): string {
    const classes = [
      'w-full',
      'px-4',
      'py-3',
      'border',
      'rounded-lg',
      'flex',
      'items-center',
      'justify-between',
      'gap-2',
      'transition-all',
      'duration-200',
      'focus:outline-none',
      'text-left'
    ];

    const sizeMap = {
      xs: 'px-2 py-1 text-xs',
      sm: 'px-3 py-2 text-sm',
      md: 'px-4 py-3 text-base',
      lg: 'px-5 py-4 text-lg',
      xl: 'px-6 py-5 text-xl'
    };
    classes.push(sizeMap[this.size()]);

    if (this.hasError) {
      classes.push('border-red-500', 'focus:border-red-500', 'focus:ring-2', 'focus:ring-red-100', 'dark:focus:ring-red-900/30');
    } else {
      classes.push('border-gray-300', 'dark:border-gray-600', 'focus:border-blue-600', 'dark:focus:border-blue-500', 'focus:ring-2', 'focus:ring-blue-100', 'dark:focus:ring-blue-900/30');
    }

    if (this.disabled()) {
      classes.push('opacity-60', 'cursor-not-allowed', 'bg-gray-50', 'dark:bg-gray-800');
    } else {
      classes.push('bg-white', 'dark:bg-gray-800', 'text-gray-900', 'dark:text-gray-100', 'hover:border-gray-400', 'dark:hover:border-gray-500');
    }

    const customClass = this.class();
    if (customClass) {
      classes.push(customClass);
    }

    return classes.join(' ');
  }

  get menuClasses(): string {
    return 'absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-hidden';
  }

  get labelClasses(): string {
    return 'block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5';
  }

  get hasError(): boolean {
    return this.touched() && !!this.errorMessage();
  }

  get displayError(): string {
    return this.errorMessage() || '';
  }

  private markAsTouched(): void {
    if (!this.touched()) {
      this.touched.set(true);
    }
  }

  writeValue(value: any): void {
    this.value.set(value);
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Handled by input signal
  }
}

